import * as React from "react";
import { useMemo } from "react";

import {
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { ArrowUpDown, ChevronDown, Plus, Loader2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TablePagination } from "@/components/ui/table-pagination";

export function DataTable({
  columns,
  data,
  searchKey,
  onSearchChange,
  pagination,
  onPaginationChange,
  pageCount,
  totalRows,
  itemLabel = "items",
  onRowClick,
  isLoading = false,
  enableRowSelection, // Removed default false
  getRowId,
  footerInfo,
  rowSelection: externalRowSelection,
  onRowSelectionChange: externalOnRowSelectionChange,
  showPagination = true,
  pageSizeOptions = [10, 15, 25, 50, 100],
  showPageSize = true,
  showQuickJumper = true,
  showPageNumbers = true,
  meta,
}) {
  const [sorting, setSorting] = React.useState([]);
  const [columnFilters, setColumnFilters] = React.useState([]);
  const [columnVisibility, setColumnVisibility] = React.useState({});
  const [internalRowSelection, setInternalRowSelection] = React.useState({});

  const rowSelection =
    externalRowSelection !== undefined
      ? externalRowSelection
      : internalRowSelection;
  const onRowSelectionChange =
    externalOnRowSelectionChange !== undefined
      ? externalOnRowSelectionChange
      : setInternalRowSelection;

  // Determine if selection is enabled:
  // explicitly true, or if we have an external selection state, or if columns have a "select" id
  const isSelectionEnabled =
    enableRowSelection ||
    externalRowSelection !== undefined ||
    columns.some((col) => col.id === "select");

  const tableColumns = useMemo(() => {
    // If selection is NOT enabled via prop, or if "select" column already exists, just return columns
    if (!enableRowSelection || columns.some((col) => col.id === "select")) {
      return columns;
    }

    // Otherwise inject the standard selection column
    return [
      {
        id: "select",
        header: ({ table }) => (
          <Checkbox
            checked={table.getIsAllPageRowsSelected()}
            onCheckedChange={(value) =>
              table.toggleAllPageRowsSelected(!!value)
            }
            aria-label="Select all"
          />
        ),
        cell: ({ row }) => (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={(value) => row.toggleSelected(!!value)}
            aria-label="Select row"
          />
        ),
        enableSorting: false,
        enableHiding: false,
      },
      ...columns,
    ];
  }, [columns, enableRowSelection]);

  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns: tableColumns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: showPagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: onRowSelectionChange,
    enableRowSelection: isSelectionEnabled,
    getRowId: getRowId,
    autoResetPageIndex: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      ...(pagination ? { pagination } : {}),
    },
    // Server-side pagination support if props provided
    manualPagination: !!pagination,
    pageCount: pageCount ?? -1,
    onPaginationChange: onPaginationChange,
    meta: meta || {},
  });

  // Handle global filter input if searchKey is provided
  const handleSearch = (event) => {
    if (onSearchChange) {
      onSearchChange(event.target.value);
    } else if (searchKey) {
      table.getColumn(searchKey)?.setFilterValue(event.target.value);
    }
  };

  return (
    <div className="w-full">
      {searchKey && (
        <div className="flex items-center p-4">
          <Input
            placeholder={`Filter ${searchKey}...`}
            value={
              onSearchChange
                ? undefined
                : (table.getColumn(searchKey)?.getFilterValue() ?? "")
            }
            onChange={handleSearch}
            className="max-w-sm"
          />
        </div>
      )}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={tableColumns.length}
                  className="h-24 text-center"
                >
                  <div className="flex justify-center items-center">
                    <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  </div>
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  onClick={(e) => {
                    // Prevent row click if clicking on an action button/menu
                    if (
                      e.target.closest("button") ||
                      e.target.closest("[role='menuitem']")
                    )
                      return;
                    onRowClick && onRowClick(row.original);
                  }}
                  className={
                    onRowClick ? "cursor-pointer hover:bg-muted/50" : ""
                  }
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={tableColumns.length}
                  className="h-24 text-center"
                >
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {showPagination && (
        <TablePagination
          table={table}
          pageCount={pageCount}
          totalRows={totalRows}
          itemLabel={itemLabel}
          pageSizeOptions={pageSizeOptions}
          showPageSize={showPageSize}
          showQuickJumper={showQuickJumper}
          showPageNumbers={showPageNumbers}
          footerInfo={footerInfo}
          isSelectionEnabled={isSelectionEnabled}
        />
      )}
    </div>
  );
}

export { TablePagination } from "@/components/ui/table-pagination";
